import React, {ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode, forwardRef, useState} from 'react'

import ToolTip from '../feedback/ToolTip.js'
import Spinner from '../feedback/Spinner.js'
import {markFieldRowControl, useFieldRowOffset} from '../forms/FormRowContext.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {buttonColors, iconButtonSizes} from './styles.js'
import {useClickGuard} from './useClickGuard.js'

ensureFocusStyles()

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'color'> {
    icon?: string
    /** Shown instead of icon */
    children?: ReactNode
    /** Style of the <button> */
    style?: CSSProperties
    type?: 'button' | 'submit' | 'reset'
    /** Colour of the icon instead of the default grey */
    color?: string
    /** No hover background (a read-only control that looks like a button) */
    noHover?: boolean
    loading?: boolean
    tooltip?: string
    /** Number shown at the top right of the icon, 9+ above 9 */
    badge?: number
    /** Lets every click through, for a button that is clicked quickly in a row on purpose (a stepper) */
    noClickGuard?: boolean
}

/**
 * A round button with an icon. The style prop and the other props (aria-*, onMouseDown...) and the ref go to the
 * <button>, the outer element holds the badge and the tooltip and places the button in a FormRow. Clicks within 200ms
 * of the previous one are ignored (unless noClickGuard), loading shows a spinner and disables the button. It is not in
 * the tab order (tabIndex -1, a caller can pass another tabIndex).
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
    icon,
    children,
    style = {},
    type = 'button',
    color,
    disabled,
    noHover,
    loading,
    tooltip,
    badge,
    noClickGuard,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    className,
    ...rest
}, ref) => {
    const [hover, setHover] = useState(false)
    const guardClick = useClickGuard()
    const fieldRowOffset = useFieldRowOffset(iconButtonSizes.size)

    const inactive = disabled || loading

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (noClickGuard || guardClick(event)) {
            onClick?.(event)
        }
    }

    const defaultStyle: CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: iconButtonSizes.size,
        height: iconButtonSizes.size,
        textAlign: 'center',
        textDecoration: 'none',
        fontSize: iconButtonSizes.iconSize,
        color: disabled ? buttonColors.iconButtonDisabled : (color || buttonColors.iconButton),
        border: 'none',
        borderRadius: iconButtonSizes.radius,
        cursor: disabled ? 'default' : 'pointer',
        transition: '0.1s all',
        ...style,
        background: !disabled && hover ? buttonColors.iconButtonHover : (style.background || 'transparent')
    }

    // The offset goes on the outer element, unless the caller placed the button itself
    const rowOffset = style.marginTop === undefined && style.margin === undefined ? fieldRowOffset : {}

    const content = (
        <>
            <button
                // Not a tab stop, like before: tabbing through a form skips the icon buttons in and next to its fields
                tabIndex={-1}
                // Named by its tooltip, unless the caller names it
                aria-label={tooltip}
                aria-busy={loading || undefined}
                {...rest}
                ref={ref}
                className={className ? `${focusableClass} ${className}` : focusableClass}
                style={defaultStyle}
                type={type}
                disabled={inactive}
                onClick={inactive ? undefined : handleClick}
                onMouseDown={loading ? undefined : onMouseDown}
                onMouseEnter={(event) => {
                    setHover(!noHover)
                    onMouseEnter?.(event)
                }}
                onMouseLeave={(event) => {
                    setHover(false)
                    onMouseLeave?.(event)
                }}
            >
                {loading ?
                    <Spinner color={buttonColors.iconButton} /> :
                    icon ?
                        <i aria-hidden className={icon} /> :
                        children
                }
            </button>

            {!!badge &&
                <i
                    aria-hidden
                    style={{position: 'absolute', top: iconButtonSizes.badgeTop, left: iconButtonSizes.badgeLeft, color: buttonColors.badge, fontSize: iconButtonSizes.badgeSize}}
                    className={badge > iconButtonSizes.badgeMax ? 'mdi mdi-numeric-9-plus-circle' : `mdi mdi-numeric-${badge}-circle`}
                />
            }
        </>
    )

    if (tooltip) {
        return (
            <ToolTip text={tooltip} style={rowOffset}>
                {content}
            </ToolTip>
        )
    }

    return (
        <div style={{position: 'relative', ...rowOffset}}>
            {content}
        </div>
    )
})

IconButton.displayName = 'IconButton'

export default markFieldRowControl(IconButton)
